/**
 * Address Card Component
 *
 * Address input card with autocomplete for searching NSW properties.
 * Supports both address and lot number search.
 * Styles match original eaidev.css exactly.
 */

import { LitElement, html, css } from 'lit';
import { propertyService } from '../services/property.service.js';
import { STORAGE_KEYS } from '../utils/storage.js';

/**
 * AddressCard - Address input component
 * @element address-card
 * @fires address-selected - When an address is selected
 * @fires address-validated - When property is validated
 * @fires address-cancel - When user cancels
 */
export class AddressCard extends LitElement {
    static properties = {
        activeTab: { type: String, state: true },
        addressQuery: { type: String }, // Can be set from outside
        lotNumber: { type: String, state: true },
        suggestions: { type: Array, state: true },
        isLoading: { type: Boolean, state: true },
        error: { type: String, state: true },
        selectedPropId: { type: String, state: true },
        isSearching: { type: Boolean, state: true },
        disabled: { type: Boolean, reflect: true },
        messageId: { type: String }
    };

    static styles = css`
        :host {
            display: block;
            width: calc(100% - 10px);
            align-self: stretch;
            font-family: 'Inter', sans-serif;
            box-sizing: border-box;
            margin-left: 40px;
        }

        * {
            box-sizing: border-box;
        }

        /* Address Card Container - from eaidev.css lines 415-421 */
        .address-card-container {
            width: 100%;
            max-width: 93%;
            box-sizing: border-box;
            overflow: hidden;
        }

        /* Address Card - from eaidev.css lines 423-429 */
        .address-card {
            background: #f8f8f8;
            border-radius: 10px;
            padding: 10px;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }

        /* Address Toggle - from eaidev.css lines 431-439 */
        .address-toggle {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            background-color: #f5f5f5;
            border-radius: 12px;
            margin-bottom: 10px;
        }

        /* Tab Button - from eaidev.css lines 441-455 */
        .tab {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            color: #111827;
            border: none;
            padding: 8px 8px;
            border-radius: 8px;
            font-weight: 500;
            font-size: 14px;
            cursor: pointer;
            transition:
                background-color 0.2s ease,
                color 0.2s ease;
        }

        /* Active Tab - from eaidev.css lines 457-461 */
        .tab.active {
            background: #ffffff;
            color: #111827;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
        }

        /* Card Body - from eaidev.css lines 472-476 */
        .address-card-body {
            margin-bottom: 8px;
            position: relative;
        }

        /* Address Field Empty - from eaidev.css lines 490-503 */
        .address-field-empty {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            font-size: 14px;
            resize: none;
            outline: none;
            background: #ffffff;
            min-height: 40px;
            height: auto;
            color: #111827;
            overflow: hidden;
            font-family: 'Inter', sans-serif;
            box-sizing: border-box;
        }

        /* Address Field Filled - from eaidev.css lines 505-516 */
        .address-field-filled {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            font-size: 14px;
            resize: none;
            outline: none;
            background: #ffffff;
            min-height: 40px;
            height: auto;
            color: #111827;
            font-family: 'Inter', sans-serif;
            box-sizing: border-box;
            overflow: hidden;
        }

        .address-field::placeholder {
            color: #999;
        }

        .input-wrapper {
            position: relative;
        }

        .input-wrapper textarea {
            padding-right: 36px;
        }

        .input-spinner {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            width: 18px;
            height: 18px;
            border: 2px solid #e5e7eb;
            border-top-color: #6b7280;
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
            to {
                transform: translateY(-50%) rotate(360deg);
            }
        }

        /* Choices Container - from eaidev.css lines 522-530 */
        .choices-container {
            background: #f8f8f8;
            overflow-y: hidden;
            margin-top: 0;
            margin-bottom: 8px;
            position: relative;
            z-index: 9999;
        }

        /* Choice Item - from eaidev.css lines 532-536 */
        .choices-container .choice {
            padding: 8px 0;
            cursor: pointer;
            margin: 0 8px;
            font-size: 14px;
        }

        /* Choice Hover - from eaidev.css lines 538-541 */
        .choices-container .choice:hover {
            background: #eaeaea;
        }

        /* Footer - from eaidev.css lines 543-546 */
        .address-card-footer {
            text-align: center;
        }

        /* Submit Button - from eaidev.css lines 548-558 */
        .submit-btn {
            width: 100%;
            height: 40px;
            background: #111111;
            color: #ffffff;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 500;
            border: none;
            cursor: pointer;
            transition: background-color 0.2s ease;
        }

        /* Submit Button Hover - from eaidev.css lines 560-563 */
        .submit-btn:hover:not(:disabled) {
            background-color: #333333;
        }

        /* Submit Button Disabled - from eaidev.css lines 565-568 */
        .submit-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    `;

    constructor() {
        super();
        this.activeTab = 'address';
        this.addressQuery = '';
        this.lotNumber = '';
        this.suggestions = [];
        this.isLoading = false;
        this.error = '';
        this.selectedPropId = '';
        this.selectedGurasId = '';
        this.isSearching = false;
        this.disabled = false;
        this.messageId = '';
        this._debounceTimer = null;
        this._activeController = null;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        clearTimeout(this._debounceTimer);
        this._abortActiveRequest();
    }

    _abortActiveRequest() {
        if (this._activeController) {
            this._activeController.abort();
            this._activeController = null;
        }
    }

    _createRequestSignal() {
        this._abortActiveRequest();
        this._activeController = new AbortController();
        return this._activeController.signal;
    }

    render() {
        const hasValue =
            this.activeTab === 'address' ? this.addressQuery.trim().length > 0 : this.lotNumber.trim().length > 0;
        const canSubmit = hasValue && this.selectedPropId && !this.disabled;

        return html`
            <div class="address-card-container">
                <div class="address-card">
                    <div class="address-card-header">
                        <div class="address-toggle">
                            <button
                                class="tab ${this.activeTab === 'address' ? 'active' : ''}"
                                ?disabled=${this.disabled || this.isLoading || !!this.error}
                                @click=${() => this._switchTab('address')}
                            >
                                Address
                            </button>
                            <button
                                class="tab ${this.activeTab === 'lot' ? 'active' : ''}"
                                ?disabled=${this.disabled || this.isLoading || !!this.error}
                                @click=${() => this._switchTab('lot')}
                            >
                                Lot number
                            </button>
                        </div>
                    </div>
                    <div class="address-card-body">
                        <div class="input-wrapper">
                            ${this.activeTab === 'address'
                                ? html`
                                      <textarea
                                          class="${hasValue ? 'address-field-filled' : 'address-field-empty'}"
                                          placeholder="Enter address"
                                          .value=${this.addressQuery}
                                          ?disabled=${this.disabled || this.isLoading || !!this.error}
                                          @input=${this._handleAddressInput}
                                          rows="1"
                                      ></textarea>
                                  `
                                : html`
                                      <textarea
                                          class="${this.lotNumber ? 'address-field-filled' : 'address-field-empty'}"
                                          placeholder="Enter lot number"
                                          .value=${this.lotNumber}
                                          ?disabled=${this.disabled || this.isLoading || !!this.error}
                                          @input=${this._handleLotInput}
                                          rows="1"
                                      ></textarea>
                                  `}
                            ${this.isSearching ? html`<div class="input-spinner"></div>` : ''}
                        </div>
                        ${!this.disabled && !this.isLoading && !this.error && this.suggestions.length > 0
                            ? html`
                                  <div class="choices-container">
                                      ${this.suggestions.map(
                                          (suggestion) => html`
                                              <div class="choice" @click=${() => this._selectSuggestion(suggestion)}>
                                                  ${suggestion.address || suggestion}
                                              </div>
                                          `
                                      )}
                                  </div>
                              `
                            : ''}
                    </div>
                    <div class="address-card-footer">
                        <button
                            class="submit-btn"
                            ?disabled=${!canSubmit || this.isLoading || this.disabled || !!this.error}
                            @click=${this._handleSubmit}
                        >
                            ${this.disabled || this.error ? 'Submitted' : this.isLoading ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    _switchTab(tab) {
        this.activeTab = tab;
        this.suggestions = [];
        this.error = '';
        // Clear selectedPropId when switching to lot tab to prevent submitting
        // with a stale selection while lot input differs
        if (tab === 'lot') {
            this.selectedPropId = '';
        }
    }

    _handleAddressInput(e) {
        this.addressQuery = e.target.value;
        this.error = '';
        this.selectedPropId = '';
        this._autoResize(e.target);

        // Debounce autocomplete
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
            this._fetchSuggestions();
        }, 300);
    }

    _handleLotInput(e) {
        this.lotNumber = e.target.value;
        this.error = '';
        this.selectedPropId = '';
        this._autoResize(e.target);

        // Debounce lot number search
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
            this._fetchLotSuggestions();
        }, 300);
    }

    _autoResize(textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    }

    async _fetchSuggestions() {
        this.isSearching = true;
        const signal = this._createRequestSignal();
        try {
            const results = await propertyService.searchAddress(this.addressQuery, 3, { signal });
            if (signal.aborted) return;
            this.suggestions = results || [];
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Failed to fetch suggestions:', error);
            this.suggestions = [];
        } finally {
            if (!signal.aborted) {
                this.isSearching = false;
            }
            if (this._activeController?.signal === signal) {
                this._activeController = null;
            }
        }
    }

    async _fetchLotSuggestions() {
        if (this.lotNumber.length < 1) {
            this.suggestions = [];
            return;
        }

        this.isSearching = true;
        this.suggestions = []; // Clear any stale address suggestions
        const signal = this._createRequestSignal();
        try {
            const result = await propertyService.searchLot(this.lotNumber, { signal });
            if (signal.aborted) return;
            if (result && result.address) {
                this.activeTab = 'address';
                this._selectSuggestion(result);
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Failed to fetch lot suggestions:', error);
            this.suggestions = []; // Clear on error
        } finally {
            if (!signal.aborted) {
                this.isSearching = false;
            }
            if (this._activeController?.signal === signal) {
                this._activeController = null;
            }
        }
    }

    _selectSuggestion(suggestion) {
        const address = suggestion.address || suggestion;
        this.addressQuery = address;
        this.suggestions = [];

        // Store propId and gurasId from suggestion. Always overwrite — picking
        // a new candidate with no gurasId must clear the previous one so we
        // never submit a stale identifier for a different property.
        if (suggestion.propId) {
            this.selectedPropId = suggestion.propId;
            sessionStorage.setItem('propId', suggestion.propId);
        }
        this.selectedGurasId = suggestion.gurasId || '';
        if (suggestion.gurasId) {
            sessionStorage.setItem(STORAGE_KEYS.GURAS_ID, suggestion.gurasId);
        } else {
            sessionStorage.removeItem(STORAGE_KEYS.GURAS_ID);
        }
        if (suggestion.address) {
            sessionStorage.setItem('address', suggestion.address);
        }

        // Auto resize textarea after selection
        this.updateComplete.then(() => {
            const textarea = this.shadowRoot.querySelector('textarea');
            if (textarea) {
                this._autoResize(textarea);
            }
        });

        this.dispatchEvent(
            new CustomEvent('address-selected', {
                detail: { address, suggestion },
                bubbles: true,
                composed: true
            })
        );
    }

    async _handleSubmit() {
        if (!this.selectedPropId || this.isLoading || this.error) {
            return;
        }

        this.isLoading = true;
        this.error = '';
        const signal = this._createRequestSignal();

        // Dispatch event to show typing indicator in chat
        this.dispatchEvent(
            new CustomEvent('address-validating', {
                bubbles: true,
                composed: true
            })
        );

        try {
            const result = await propertyService.validateProperty(
                this.addressQuery,
                this.selectedPropId,
                this.selectedGurasId,
                { signal }
            );
            if (signal.aborted) return;

            if (result?.council_name?.trim()) {
                // Note: isLoading stays true - parent will call setSubmitComplete() when done
                this.dispatchEvent(
                    new CustomEvent('address-validated', {
                        detail: {
                            result,
                            address: this.addressQuery,
                            propId: this.selectedPropId,
                            gurasId: this.selectedGurasId
                        },
                        bubbles: true,
                        composed: true
                    })
                );
            } else {
                this.isLoading = false;
                this.error =
                    "I couldn't confirm this property's council or retrieve its planning information. This may be temporary. Please restart the chat.";
                this.dispatchEvent(
                    new CustomEvent('address-validation-error', {
                        detail: {
                            error: 'lookup_unavailable',
                            restartRequired: true,
                            message: this.error,
                            address: this.addressQuery
                        },
                        bubbles: true,
                        composed: true
                    })
                );
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Validation error:', error);
            this.isLoading = false;
            this.error = 'Property details are unavailable at the moment. Please restart the chat.';
            this.dispatchEvent(
                new CustomEvent('address-validation-error', {
                    detail: {
                        error: 'api_error',
                        restartRequired: true,
                        message: this.error,
                        address: this.addressQuery
                    },
                    bubbles: true,
                    composed: true
                })
            );
        } finally {
            if (this._activeController?.signal === signal) {
                this._activeController = null;
            }
        }
    }

    /**
     * Track an invalid address attempt in sessionStorage
     */
    _trackInvalidAddress() {
        if (!this.messageId || !this.addressQuery) return;
        try {
            const stored = JSON.parse(sessionStorage.getItem('invalidAddressCards') || '[]');
            stored.push({ id: this.messageId, invalidValue: this.addressQuery });
            sessionStorage.setItem('invalidAddressCards', JSON.stringify(stored));
        } catch {
            // ignore storage errors
        }
    }

    /**
     * Called by parent when validation processing is complete
     * This re-enables the submit button
     */
    setSubmitComplete() {
        this.isLoading = false;
    }

    _handleCancel() {
        this.dispatchEvent(
            new CustomEvent('address-cancel', {
                bubbles: true,
                composed: true
            })
        );
    }
}

customElements.define('address-card', AddressCard);
